import React, { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEvent from "react-use-event-hook";

import { jwtDecode } from "jwt-decode";

import { LOCAL_STORAGE } from "@constants";
import { getSmartlook } from "@services/axios/safeSmartlook";

import useFeatureFlags from "@hooks/useFeatureFlags";
import useMessage from "@hooks/useMessage";
import {
  UseRefreshTokenResult,
  parseTokenExpireDate,
  useRefreshToken,
} from "@hooks/useRefreshToken";
import { PRODUCT_CODE, PRODUCT_TYPE } from "@utils/enums";
import { getProductTypeFromProductCode } from "@utils/product";

import { useAlerts } from "./Alerts";

type Product = Record<
  PRODUCT_TYPE,
  {
    name: string;
    productType: PRODUCT_TYPE;
    productCode?: PRODUCT_CODE;
    blocked: boolean;
  }
>;

export type User = {
  accessToken?: string | null;
  refreshToken?: string | null;
  tokenHasDataOfCorbanId?: number | null;
  name?: string | null;
  uniqueId?: string | null;
  identifier?: string | null;
  enableFaceMatch?: boolean | null;
  isChecked?: boolean | null;
  checkUrl?: string | null;
  supportChat?: boolean | null;
  allowIlliterate?: boolean | null;
  allowLegalRepresentative?: boolean | null;
  allowCardLegalRepresentative?: boolean | null;
  allowCreateUser?: boolean | null;
  corbanId?: number | null;
  corbanName?: string | null;
  products?: Partial<Product>;
  corbanProducts?: Partial<Product>;
  id?: number | null;
  crcpInfo?: {
    status: "active" | "deactivated" | "coming_soon";
    alerts?: Array<{
      id: string;
      value: string;
      status: "ATIVO" | "INATIVO" | "EXPIRANDO";
    }> | null;
  } | null;
  mcbInfo?: {
    scores?: {
      monthly?: number;
      accumulated?: number;
    };
    corban: {
      status: "active" | "deactivated";
      alerts?: Array<{
        id: string;
        value: string;
        status: "ATIVO" | "SUSPENSO";
      }> | null;
    };
    user: {
      status: "active" | "deactivated";
      alerts?: Array<{
        id: string;
        value: string;
        status: "ATIVO" | "SUSPENSO";
      }> | null;
    };
  } | null;
};

type ProductTypeList = Array<{
  nome: string;
  tipo_produto: PRODUCT_TYPE;
  codigo_produto?: PRODUCT_CODE;
  bloqueado?: boolean;
}>;

type AuthContext = {
  user: User | null;
  accessToken: string;
  name: string;
  identifier: string;
  loading: boolean;
  permissions: {
    allowIlliterate: boolean;
    allowLegalRepresentative: boolean;
    allowCardLegalRepresentative: boolean;
  };
  updateUser: (user?: Partial<User>) => void;
  clearUser: () => void;
  setProducts: (productTypeList: ProductTypeList) => void;
  setCorbanProducts: (productTypeList: ProductTypeList) => void;
  hasProduct: (productCode: PRODUCT_CODE) => boolean;
  hasProductBlocked: (productCode: PRODUCT_CODE) => boolean;
  onLogout: (redirectTo?: string) => void;
  onExpireSession: () => void;
  refreshAccessToken: UseRefreshTokenResult["refreshAccessToken"];
  ensureAccessToken: UseRefreshTokenResult["ensureAccessToken"];
};

export const AuthContext = createContext<AuthContext>({
  user: null,
  accessToken: "",
  name: "",
  identifier: "",
  loading: true,
  permissions: {
    allowIlliterate: false,
    allowLegalRepresentative: false,
    allowCardLegalRepresentative: false,
  },
  updateUser: () => {
    //
  },
  clearUser: () => {
    //
  },
  setProducts: () => {
    //
  },
  setCorbanProducts: () => {
    //
  },
  hasProduct: () => false,
  hasProductBlocked: () => false,
  onLogout: () => {
    //
  },
  onExpireSession: () => {
    //
  },
  refreshAccessToken: () => Promise.resolve(false),
  ensureAccessToken: () => Promise.resolve(false),
});

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { featureFlags, fetchFeatureFlags } = useFeatureFlags();
  const navigate = useNavigate();
  const message = useMessage();
  const { addAlert, clearAlerts } = useAlerts();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const enableValidationCRCP = !!featureFlags.enableValidationCRCP;
  const enableValidationMCB = !!featureFlags.enableValidationMCB;

  const accessToken = user?.accessToken || "";
  const refreshToken = user?.refreshToken || "";

  const handleUserCertificateAlert = useEvent((data: User["crcpInfo"]) => {
    if (!data || !enableValidationCRCP) return;
    const { status: type, alerts } = data;
    let alert: Parameters<typeof addAlert>[0] | null = null;

    switch (type) {
      case "coming_soon": {
        const alertExpires = alerts?.find(
          ({ status }) => status === "EXPIRANDO",
        );
        if (alertExpires) {
          alert = {
            id: alertExpires?.id,
            message: {
              title: "Fique ligado com seu certificado(s)",
              description: (
                <>
                  <ul>
                    {alertExpires?.value?.split("-&-").map((value, id) => (
                      <li key={`certificate-alert-${id}`}>
                        <strong>{value}</strong>
                      </li>
                    ))}
                  </ul>
                  <span style={{ color: "black" }}>
                    A digitação dos seus produtos{" "}
                    <strong>será bloqueada</strong> caso o(s) certificado(s) não
                    sejam renovados. Realize a renovação desse(s) certificado(s)
                    acessando:{" "}
                    <a
                      href="https://aneps.org.br/produto/certificacao-plus/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "black", textDecoration: "underline" }}
                    >
                      <strong>ANEPS Certificação Plus</strong>
                    </a>
                    {" e "}
                    <a
                      href="https://febrabaneducacao.com.br/certificacoes/correspondentes"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "black", textDecoration: "underline" }}
                    >
                      <strong>FBB100 - Certificação Completa + LGPD</strong>
                    </a>
                    <br />
                    Dúvidas, entre em contato com o{" "}
                    <strong>seu apoio comercial Amigoz</strong>
                  </span>
                </>
              ),
            },
            type: "warning",
            important: true,
          };
        }
        break;
      }
      case "deactivated": {
        const alertExpires = alerts?.find(({ status }) => status === "INATIVO");
        if (alertExpires) {
          alert = {
            id: alertExpires?.id,
            message: {
              title: "Digitação bloqueada",
              description: (
                <>
                  {alertExpires?.value?.length ? (
                    <>
                      <ul>
                        {alertExpires?.value?.split("-&-").map((value, id) => (
                          <li key={`certificate-alert-${id}`}>
                            <strong>{value}</strong>
                          </li>
                        ))}
                      </ul>
                      <span style={{ color: "black" }}>
                        A digitação dos seus produtos{" "}
                        <strong>está bloqueada</strong>. Para desbloquear,
                        realize a renovação desse(s) certificado(s) acessando:{" "}
                        <a
                          href="https://aneps.org.br/produto/certificacao-plus/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "black",
                            textDecoration: "underline",
                          }}
                        >
                          <strong>ANEPS Certificação Plus</strong>
                        </a>
                        {" e "}
                        <a
                          href="https://febrabaneducacao.com.br/certificacoes/correspondentes"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "black",
                            textDecoration: "underline",
                          }}
                        >
                          <strong>FBB100 - Certificação Completa + LGPD</strong>
                        </a>
                        <br />
                        Dúvidas, entre em contato com o seu{" "}
                        <strong>apoio comercial Amigoz</strong>
                      </span>
                    </>
                  ) : (
                    <span style={{ color: "black" }}>
                      Não identificamos certificados vinculados ao seu CPF,
                      sendo assim a digitação dos seus produtos{" "}
                      <strong>está bloqueada</strong>
                      <br />
                      Para desbloquear, realize a certificação acessando:{" "}
                      <a
                        href="https://aneps.org.br/produto/certificacao-plus/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "black", textDecoration: "underline" }}
                      >
                        <strong>ANEPS Certificação Plus</strong>
                      </a>
                      {" e "}
                      <a
                        href="https://febrabaneducacao.com.br/certificacoes/correspondentes"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "black", textDecoration: "underline" }}
                      >
                        <strong>FBB100 - Certificação Completa + LGPD</strong>
                      </a>
                      <br />
                      Demais consultas no Portal Amigoz, seguem liberadas
                      <br />
                      Dúvidas, entre em contato com o{" "}
                      <strong>seu apoio comercial Amigoz</strong>
                    </span>
                  )}
                </>
              ),
            },
            type: "error",
            important: true,
          };
        }
        break;
      }
      case "active":
      default:
        return;
    }

    if (alert) {
      addAlert(alert);
    }
  });

  const handleMCBAlert = useEvent((data: User["mcbInfo"]) => {
    if (!data || !enableValidationMCB) return;
    const alerts: Array<Parameters<typeof addAlert>[0]> = [];

    const { status: corbanStatus, alerts: corbanAlerts } = data.corban;
    if (corbanStatus === "deactivated") {
      const alertSuspense = corbanAlerts?.find(
        ({ status }) => status === "SUSPENSO",
      );
      if (alertSuspense) {
        alerts.push({
          id: alertSuspense.id,
          message: {
            title: "Digitação bloqueada no MCB",
            description: (
              <>
                {alertSuspense?.value?.length && (
                  <>
                    <ul>
                      {alertSuspense?.value
                        ?.split("-&-")
                        .map((value, id) => (
                          <li key={`mcb-alert-${id}`}>{value}</li>
                        ))}
                    </ul>
                    <span style={{ color: "black" }}>
                      A digitação dos seus produtos{" "}
                      <strong>está bloqueada</strong>.
                    </span>
                  </>
                )}
              </>
            ),
          },
          type: "error",
          important: true,
        });
      }
    }

    const { status: userStatus, alerts: userAlerts } = data.user;
    if (userStatus === "deactivated") {
      const alertSuspense = userAlerts?.find(
        ({ status }) => status === "SUSPENSO",
      );
      if (alertSuspense) {
        alerts.push({
          id: alertSuspense.id,
          message: {
            title: "Digitação bloqueada no MCB",
            description: (
              <>
                {alertSuspense?.value?.length && (
                  <>
                    <ul>
                      {alertSuspense?.value
                        ?.split("-&-")
                        .map((value, id) => (
                          <li key={`mcb-alert-${id}`}>{value}</li>
                        ))}
                    </ul>
                    <span style={{ color: "black" }}>
                      A digitação dos seus produtos{" "}
                      <strong>está bloqueada</strong>.
                    </span>
                  </>
                )}
              </>
            ),
          },
          type: "error",
          important: true,
        });
      }
    }

    if (alerts.length) {
      alerts.forEach((alert) => addAlert(alert));
    }
  });

  const updateUser = useCallback(
    (userData?: Partial<User>) => {
      setUser((curUser) => {
        if (curUser || userData) {
          const newUser: User = {
            ...(curUser || {}),
            ...(userData || {}),
          };

          void smartlookIdentify(newUser);
          if (userData?.crcpInfo) {
            handleUserCertificateAlert(userData?.crcpInfo);
          }

          if (userData?.mcbInfo) {
            handleMCBAlert(userData?.mcbInfo);
          }

          const userDataStr = JSON.stringify(newUser);
          localStorage.setItem(LOCAL_STORAGE.USER, userDataStr);

          return newUser;
        } else {
          localStorage.removeItem(LOCAL_STORAGE.USER);
          void smartlookAnonymize();
          return null;
        }
      });
    },
    [handleUserCertificateAlert, handleMCBAlert],
  );

  const clearUser = useEvent(() => {
    setUser(null);
    clearAlerts();
    localStorage.removeItem(LOCAL_STORAGE.USER);
    localStorage.removeItem(LOCAL_STORAGE.FEATURE_FLAGS);
    void smartlookAnonymize();
  });

  const productTypeListToMap = useCallback(
    (
      productTypeList: ProductTypeList,
      crcpInfo?: User["crcpInfo"],
      mcbInfo?: User["mcbInfo"],
    ) => {
      /** Used to block, or not, consigned products */
      const BLOCKED_ALL_PRODUCTS = true;
      const allowedProducts: PRODUCT_CODE[] = BLOCKED_ALL_PRODUCTS
        ? []
        : [PRODUCT_CODE.CONSIGNADO_PUBLICO];
      const isBlockedCRCP =
        enableValidationCRCP && crcpInfo?.status === "deactivated";
      const isBlockedMCB =
        (enableValidationMCB && mcbInfo?.corban.status === "deactivated") ||
        mcbInfo?.user.status === "deactivated";

      return productTypeList.reduce<NonNullable<User["products"]>>(
        (acc, product) => {
          let blockedByCRCP = isBlockedCRCP;
          if (blockedByCRCP && product?.codigo_produto) {
            blockedByCRCP = !allowedProducts.includes(product?.codigo_produto);
          }
          let blockedByMCB = isBlockedMCB;
          if (blockedByMCB && product?.codigo_produto) {
            blockedByMCB = !allowedProducts.includes(product?.codigo_produto);
          }

          acc[product.tipo_produto] = {
            name: product.nome,
            productType: product.tipo_produto,
            productCode: product.codigo_produto,
            blocked: !!product.bloqueado || blockedByCRCP || blockedByMCB,
          } satisfies Product[PRODUCT_TYPE];
          return acc;
        },
        {},
      ) satisfies Partial<Product>;
    },
    [enableValidationCRCP, enableValidationMCB],
  );

  const setProducts: AuthContext["setProducts"] = useCallback(
    (productTypeList) => {
      setUser((curUser) => {
        return {
          ...curUser,
          products: productTypeListToMap(
            productTypeList,
            curUser?.crcpInfo,
            curUser?.mcbInfo,
          ),
        };
      });
    },
    [productTypeListToMap],
  );

  const setCorbanProducts: AuthContext["setCorbanProducts"] = useCallback(
    (productTypeList) => {
      setUser((curUser) => ({
        ...curUser,
        corbanProducts: productTypeListToMap(
          productTypeList,
          curUser?.crcpInfo,
          curUser?.mcbInfo,
        ),
      }));
    },
    [productTypeListToMap],
  );

  const hasProduct = useCallback(
    (productCode: PRODUCT_CODE) => {
      if (!accessToken) {
        return false;
      }

      const jwt = jwtDecode<{ products_allowed?: Array<PRODUCT_CODE> }>(
        accessToken,
      );
      return !!jwt?.products_allowed?.includes(productCode);
    },
    [accessToken],
  );

  const hasProductBlocked = useCallback(
    (productCode: PRODUCT_CODE) => {
      const productType = getProductTypeFromProductCode(productCode);
      return !!user?.products?.[productType]?.blocked;
    },
    [user],
  );

  const onUpdateAccessToken = useCallback(
    (accessToken: string) => updateUser({ accessToken }),
    [updateUser],
  );

  const onLogout = useCallback(
    (redirectTo?: string) => {
      clearUser();

      if (redirectTo && typeof redirectTo === "string") {
        location.href = redirectTo;
      } else if (location.pathname !== "/login") {
        navigate("/login");
      }
    },
    [clearUser, navigate],
  );

  const onExpireSession = useCallback(() => {
    onLogout();
    void message.error("Sua sessão expirou, faça o login novamente.");
  }, [onLogout, message]);

  const { refreshAccessToken, ensureAccessToken } = useRefreshToken({
    accessToken,
    refreshToken,
    onUpdateAccessToken,
    onExpireSession,
  });

  const smartlookIdentify = async (user: User) => {
    const Smartlook = await getSmartlook();

    if (Smartlook.initialized()) {
      if (user.identifier) {
        Smartlook.identify(user.identifier, {
          name: user.name || "",
        });
      }
    }
  };

  const smartlookAnonymize = async () => {
    const Smartlook = await getSmartlook();

    if (Smartlook.initialized()) {
      Smartlook.anonymize();
    }
  };

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== LOCAL_STORAGE.USER) return;

      if (e.oldValue && !e.newValue) onLogout();
      if (e.newValue && e.oldValue !== e.newValue) {
        updateUser(JSON.parse(e.newValue) as User);
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [onLogout, updateUser]);

  useEffect(() => {
    const userData = localStorage.getItem(LOCAL_STORAGE.USER);

    if (userData) {
      const user = JSON.parse(userData) as User;

      const isRefreshTokenExpired =
        parseTokenExpireDate(user.refreshToken) - Date.now() < 0;
      const isAccessTokenExpired =
        parseTokenExpireDate(user.accessToken) - Date.now() < 0;

      if (isRefreshTokenExpired || isAccessTokenExpired) {
        onExpireSession();
      } else {
        setUser(user);
        if (user.crcpInfo) {
          handleUserCertificateAlert(user.crcpInfo);
        }
        if (user.mcbInfo) {
          handleMCBAlert(user.mcbInfo);
        }
        void smartlookIdentify(user);
      }
    }

    setLoading(false);
  }, [onExpireSession, handleUserCertificateAlert, handleMCBAlert]);

  useEffect(() => {
    const featureFlagsStored = localStorage.getItem(
      LOCAL_STORAGE.FEATURE_FLAGS,
    );

    if (!featureFlagsStored || (user === null && !loading)) {
      void fetchFeatureFlags();
    }
  }, [fetchFeatureFlags, user, loading]);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        name: user?.name || "",
        identifier: user?.identifier || "",
        loading,
        permissions: {
          allowIlliterate: user?.allowIlliterate || false,
          allowLegalRepresentative: user?.allowLegalRepresentative || false,
          allowCardLegalRepresentative:
            user?.allowCardLegalRepresentative || false,
        },
        updateUser,
        clearUser,
        setProducts,
        setCorbanProducts,
        hasProduct,
        onLogout,
        onExpireSession,
        refreshAccessToken,
        ensureAccessToken,
        hasProductBlocked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
